'use client'

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from './AuthContext'

const AppDataContext = createContext(null)

const CACHE_TTL = 30 * 60 * 1000 // 30 min

function cacheKey(uid) { return `ff_cache_${uid}` }

function readCache(uid) {
  try {
    const raw = sessionStorage.getItem(cacheKey(uid))
    if (!raw) return null
    const { profile, deals, ts } = JSON.parse(raw)
    if (Date.now() - ts > CACHE_TTL) return null
    return { profile, deals }
  } catch { return null }
}

function writeCache(uid, profile, deals) {
  try {
    const toStore = Array.isArray(deals) ? deals.slice(0, 200) : deals
    sessionStorage.setItem(cacheKey(uid), JSON.stringify({ profile, deals: toStore, ts: Date.now() }))
  } catch {}
}

export function AppDataProvider({ children }) {
  const { currentUser, accessToken } = useAuth()

  const [destinations, setDestinations] = useState(null)
  const [airlines, setAirlines] = useState(null)
  const [profile, setProfile] = useState(null)
  const [deals, setDeals] = useState(null)
  const [globalStats, setGlobalStats] = useState(null)
  const [dataLoading, setDataLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      fetch('/api/destinations').then(r => r.json()).catch(() => []),
      fetch('/api/airlines').then(r => r.json()).catch(() => []),
    ]).then(([dests, air]) => {
      setDestinations(dests)
      setAirlines(air)
    })
  }, [])

  useEffect(() => {
    if (!currentUser || !accessToken) {
      setProfile(null)
      setDeals(null)
      setDataLoading(false)
      return
    }

    const uid = currentUser.uid

    // Serve cache immediately — no blank page on refresh
    const cached = readCache(uid)
    if (cached) {
      setProfile(cached.profile)
      setDeals(cached.deals)
      setDataLoading(false)
    } else {
      setDataLoading(true)
    }

    // Always fetch fresh in background
    const h = { Authorization: `Bearer ${accessToken}` }
    Promise.all([
      fetch('/api/profile', { headers: h }).then(r => r.ok ? r.json() : null),
      fetch('/api/deals',   { headers: h }).then(r => r.json()),
    ]).then(([p, d]) => {
      setProfile(p)
      setDeals(d?.deals ?? [])
      if (d?.globalStats) setGlobalStats(d.globalStats)
      writeCache(uid, p, d?.deals ?? [])
    }).catch(() => {
      if (!cached) setDeals([])
    }).finally(() => setDataLoading(false))
  }, [currentUser, accessToken])

  const updateProfile = useCallback((data) => {
    setProfile(p => {
      const next = p ? { ...p, ...data } : data
      if (currentUser?.uid) writeCache(currentUser.uid, next, null)
      return next
    })
  }, [currentUser])

  const refreshDeals = useCallback(async () => {
    if (!accessToken) return
    const d = await fetch('/api/deals', { headers: { Authorization: `Bearer ${accessToken}` } }).then(r => r.json()).catch(() => ({}))
    const fresh = d?.deals ?? []
    setDeals(fresh)
    if (currentUser?.uid) writeCache(currentUser.uid, profile, fresh)
  }, [accessToken, currentUser, profile])

  const value = useMemo(() => ({
    profile, deals, destinations, airlines, globalStats, dataLoading, accessToken,
    updateProfile, refreshDeals,
  }), [profile, deals, destinations, airlines, globalStats, dataLoading, accessToken, updateProfile, refreshDeals])

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  )
}

export function useAppData() {
  return useContext(AppDataContext)
}
